import { useState, useMemo, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Factory, Play, Check, Ban, Eye, X, Loader2, AlertTriangle, Info,
  ThumbsUp, ThumbsDown, Search, Filter, RefreshCw,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatDateTime, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

interface OPEtapa {
  id: number
  etapa_codigo: string
  etapa_nome: string
  etapa_cor: string
  ordem: number
  status: string
}

interface OP {
  id: number
  numero: number
  produto_id: number
  produto_descricao: string | null
  ficha_id: number | null
  variante: string
  maquina_id: number | null
  molde_id: number | null
  deposito_id: number
  pedido_id: number | null
  pedido_numero: number | null
  quantidade: string
  quantidade_produzida: string
  quantidade_refugo: string
  custo_previsto: string
  custo_real: string
  status: string
  iniciada_em: string | null
  concluida_em: string | null
  observacao: string | null
  etapas: OPEtapa[]
}

interface Produto {
  id: number
  codigo: string
  descricao: string
  estoque_atual: string | number
}

interface Maquina { id: number; codigo: string; nome: string; tipo: string; status: string }
interface Molde { id: number; codigo: string; descricao: string }
interface Deposito { id: number; nome: string; is_default: boolean; ativo: boolean }

interface PreviewLinha {
  materia_prima_id: number
  mp_codigo: string
  mp_nome: string
  unidade: string
  quantidade_necessaria: string
  saldo_atual: string
  suficiente: boolean
  custo_unitario: string
  custo_total: string
}

interface Preview {
  op_id: number
  ficha_id: number | null
  variante: string
  quantidade: string
  linhas: PreviewLinha[]
  custo_total: string
  tudo_suficiente: boolean
  ficha_encontrada: boolean
  alertas: string[]
}

const STATUS_COR: Record<string, string> = {
  proposta: 'bg-sky-100 text-sky-700',
  planejada: 'bg-ink-200 text-ink-700',
  em_producao: 'bg-amber-100 text-amber-800',
  concluida: 'bg-emerald-100 text-emerald-800',
  cancelada: 'bg-rose-100 text-rose-700',
}

export default function OrdensProducaoPage() {
  const qc = useQueryClient()
  const nav = useNavigate()
  const [status, setStatus] = useState<string>('')
  const [busca, setBusca] = useState('')
  const [maquinaId, setMaquinaId] = useState<number | ''>('')
  const [depositoId, setDepositoId] = useState<number | ''>('')
  const [variante, setVariante] = useState('')
  const [criadoDe, setCriadoDe] = useState('')
  const [criadoAte, setCriadoAte] = useState('')
  const [fimDe, setFimDe] = useState('')
  const [fimAte, setFimAte] = useState('')
  const [valorMin, setValorMin] = useState('')
  const [valorMax, setValorMax] = useState('')
  const [vinculo, setVinculo] = useState<'' | 'com_pedido' | 'sem_pedido' | 'sem_ficha'>('')
  const [atrasada, setAtrasada] = useState(false)
  const [filtrosAbertos, setFiltrosAbertos] = useState(false)
  const [showNova, setShowNova] = useState(false)
  const [iniciarAlvo, setIniciarAlvo] = useState<OP | null>(null)
  const [concluirAlvo, setConcluirAlvo] = useState<OP | null>(null)
  const [cancelarAlvo, setCancelarAlvo] = useState<OP | null>(null)

  const params = useMemo(() => {
    const p: Record<string, any> = {}
    if (status) p.status = status
    if (busca.trim()) p.q = busca.trim()
    if (maquinaId) p.maquina_id = maquinaId
    if (depositoId) p.deposito_id = depositoId
    if (variante.trim()) p.variante = variante.trim()
    if (criadoDe) p.criado_de = criadoDe
    if (criadoAte) p.criado_ate = criadoAte
    if (fimDe) p.fim_de = fimDe
    if (fimAte) p.fim_ate = fimAte
    if (valorMin) p.valor_min = valorMin
    if (valorMax) p.valor_max = valorMax
    if (vinculo === 'com_pedido') p.com_pedido = true
    if (vinculo === 'sem_pedido') p.sem_pedido = true
    if (vinculo === 'sem_ficha') p.sem_ficha = true
    if (atrasada) p.atrasada = true
    return p
  }, [status, busca, maquinaId, depositoId, variante, criadoDe, criadoAte, fimDe, fimAte, valorMin, valorMax, vinculo, atrasada])

  const filtrosAtivos = useMemo(() => {
    let n = 0
    if (busca.trim()) n++
    if (maquinaId) n++
    if (depositoId) n++
    if (variante.trim()) n++
    if (criadoDe || criadoAte) n++
    if (fimDe || fimAte) n++
    if (valorMin || valorMax) n++
    if (vinculo) n++
    if (atrasada) n++
    return n
  }, [busca, maquinaId, depositoId, variante, criadoDe, criadoAte, fimDe, fimAte, valorMin, valorMax, vinculo, atrasada])

  const limparFiltros = () => {
    setBusca(''); setMaquinaId(''); setDepositoId(''); setVariante('')
    setCriadoDe(''); setCriadoAte(''); setFimDe(''); setFimAte('')
    setValorMin(''); setValorMax(''); setVinculo(''); setAtrasada(false)
  }

  const { data: ops = [], isLoading, refetch } = useQuery<OP[]>({
    queryKey: ['ops', params],
    queryFn: async () => (await api.get('/industrial/ordens', { params })).data,
  })

  const { data: maquinasFiltro = [] } = useQuery<Maquina[]>({
    queryKey: ['maquinas-filtro'],
    queryFn: async () => (await api.get('/industrial/maquinas')).data,
  })
  const { data: depositosFiltro = [] } = useQuery<Deposito[]>({
    queryKey: ['depositos-filtro'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })

  const { data: contagem = {} } = useQuery<Record<string, number>>({
    queryKey: ['ops-contagem'],
    queryFn: async () => (await api.get('/industrial/ordens-contagem')).data,
    refetchInterval: 30000,
  })

  const aprovar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/industrial/ordens/${id}/aprovar`)).data,
    onSuccess: () => {
      toast.success('Proposta aprovada — OP agora é planejada')
      qc.invalidateQueries({ queryKey: ['ops'] })
      qc.invalidateQueries({ queryKey: ['ops-contagem'] })
    },
  })
  const rejeitar = useMutation({
    mutationFn: async ({ id, motivo }: { id: number; motivo: string }) =>
      (await api.post(`/industrial/ordens/${id}/rejeitar`, { motivo })).data,
    onSuccess: () => {
      toast.success('Proposta rejeitada')
      qc.invalidateQueries({ queryKey: ['ops'] })
      qc.invalidateQueries({ queryKey: ['ops-contagem'] })
    },
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Factory className="text-brand-600" /> Ordens de Produção
          </h1>
          <p className="text-ink-600 text-sm">
            Planeje, execute e conclua OPs. Consumo de MP automático via ficha técnica.
          </p>
        </div>
        <button onClick={() => setShowNova(true)} className="btn-primary">
          <Plus size={16} /> Nova OP
        </button>
      </div>

      {contagem.proposta > 0 && (
        <div className="card card-body bg-sky-50 border-sky-200 flex items-center gap-3">
          <AlertTriangle className="text-sky-600 shrink-0" size={20} />
          <div className="text-sm text-sky-900 flex-1">
            <strong>{contagem.proposta}</strong> {contagem.proposta === 1 ? 'proposta aguardando' : 'propostas aguardando'} aprovação do PCP.
          </div>
          <button onClick={() => setStatus('proposta')} className="btn-secondary !py-1 text-xs">
            Ver propostas
          </button>
        </div>
      )}

      <div className="card card-body space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-ink-600">Status:</span>
          {['', 'proposta', 'planejada', 'em_producao', 'concluida', 'cancelada'].map(s => (
            <button
              key={s || 'todos'}
              onClick={() => setStatus(s)}
              className={`px-3 py-1 rounded text-xs flex items-center gap-1.5 ${status === s ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}
            >
              {s === '' ? 'Todos' : s.replace('_', ' ')}
              {s && contagem[s] > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded text-[10px] ${status === s ? 'bg-white text-brand-700' : 'bg-ink-300 text-ink-700'}`}>
                  {contagem[s]}
                </span>
              )}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setAtrasada(a => !a)}
              className={`px-3 py-1 rounded text-xs flex items-center gap-1.5 ${atrasada ? 'bg-rose-600 text-white' : 'bg-rose-50 text-rose-700 hover:bg-rose-100'}`}
              title="OPs com fim previsto vencido (não concluída/cancelada)"
            >
              <AlertTriangle size={12} /> Atrasadas
            </button>
            <button
              onClick={() => setVinculo(v => v === 'sem_ficha' ? '' : 'sem_ficha')}
              className={`px-3 py-1 rounded text-xs ${vinculo === 'sem_ficha' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
              title="OPs sem ficha técnica vinculada"
            >
              Sem ficha
            </button>
            <button
              onClick={() => setFiltrosAbertos(o => !o)}
              className={`px-3 py-1 rounded text-xs flex items-center gap-1.5 ${filtrosAbertos || filtrosAtivos > 0 ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}
            >
              <Filter size={12} /> Filtros{filtrosAtivos > 0 && ` (${filtrosAtivos})`}
            </button>
            <button onClick={() => refetch()} className="p-1 rounded hover:bg-ink-100 text-ink-600" title="Atualizar">
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              type="text"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              onKeyDown={enterAbrePrimeiro(ops, op => nav(`/industrial/ordens/${op.id}`))}
              placeholder="Buscar # OP ou produto..."
              className="w-full pl-9 pr-3 py-1.5 border border-ink-300 rounded text-sm"
            />
          </div>
          {filtrosAtivos > 0 && (
            <button onClick={limparFiltros} className="text-xs text-ink-500 hover:text-rose-600 flex items-center gap-1">
              <X size={12} /> Limpar filtros
            </button>
          )}
        </div>

        {filtrosAbertos && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-ink-200">
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-ink-500 font-medium mb-1">Máquina</label>
              <select value={maquinaId} onChange={e => setMaquinaId(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-2 py-1.5 border border-ink-300 rounded text-sm">
                <option value="">Todas</option>
                {maquinasFiltro.map(m => <option key={m.id} value={m.id}>{m.codigo} — {m.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-ink-500 font-medium mb-1">Depósito</label>
              <select value={depositoId} onChange={e => setDepositoId(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-2 py-1.5 border border-ink-300 rounded text-sm">
                <option value="">Todos</option>
                {depositosFiltro.filter(d => d.ativo).map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-ink-500 font-medium mb-1">Variante</label>
              <input type="text" value={variante} onChange={e => setVariante(e.target.value.toUpperCase())}
                placeholder="PADRAO, TIPO 1..."
                className="w-full px-2 py-1.5 border border-ink-300 rounded text-sm" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-ink-500 font-medium mb-1">Vínculo</label>
              <select value={vinculo} onChange={e => setVinculo(e.target.value as any)}
                className="w-full px-2 py-1.5 border border-ink-300 rounded text-sm">
                <option value="">Qualquer</option>
                <option value="com_pedido">Com pedido</option>
                <option value="sem_pedido">Sem pedido</option>
                <option value="sem_ficha">Sem ficha técnica</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-ink-500 font-medium mb-1">Criada de</label>
              <input type="date" value={criadoDe} onChange={e => setCriadoDe(e.target.value)}
                className="w-full px-2 py-1.5 border border-ink-300 rounded text-sm" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-ink-500 font-medium mb-1">Criada até</label>
              <input type="date" value={criadoAte} onChange={e => setCriadoAte(e.target.value)}
                className="w-full px-2 py-1.5 border border-ink-300 rounded text-sm" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-ink-500 font-medium mb-1">Prazo de</label>
              <input type="date" value={fimDe} onChange={e => setFimDe(e.target.value)}
                className="w-full px-2 py-1.5 border border-ink-300 rounded text-sm" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-ink-500 font-medium mb-1">Prazo até</label>
              <input type="date" value={fimAte} onChange={e => setFimAte(e.target.value)}
                className="w-full px-2 py-1.5 border border-ink-300 rounded text-sm" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-ink-500 font-medium mb-1">Custo previsto mín. (R$)</label>
              <input type="number" min="0" step="0.01" value={valorMin} onChange={e => setValorMin(e.target.value)}
                className="w-full px-2 py-1.5 border border-ink-300 rounded text-sm" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-ink-500 font-medium mb-1">Custo previsto máx. (R$)</label>
              <input type="number" min="0" step="0.01" value={valorMax} onChange={e => setValorMax(e.target.value)}
                className="w-full px-2 py-1.5 border border-ink-300 rounded text-sm" />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-ink-500 pt-1">
          <span>{ops.length} OP(s) listada(s){ops.length === 200 && ' — limite (refine os filtros)'}</span>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Produto</th>
              <th>Variante</th>
              <th className="text-right">Qtd</th>
              <th className="text-right">Produzida</th>
              <th className="text-right">Refugo</th>
              <th>Pedido</th>
              <th className="text-right">Custo prev.</th>
              <th className="text-right">Custo real</th>
              <th>Status</th>
              <th>Iniciada em</th>
              <th className="w-48"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={12} className="text-center py-8">Carregando…</td></tr>}
            {!isLoading && ops.length === 0 && (
              <tr><td colSpan={12} className="text-center py-8 text-ink-500">
                Nenhuma OP encontrada. Clique em "Nova OP" para começar.
              </td></tr>
            )}
            {ops.map(op => (
              <tr key={op.id}
                onClick={() => nav(`/industrial/ordens/${op.id}`)}
                className="hover:bg-violet-50 cursor-pointer">
                <td className="font-mono">#{op.numero}</td>
                <td>
                  <div className="text-sm font-medium">{op.produto_descricao || `Produto #${op.produto_id}`}</div>
                  {op.ficha_id && <div className="text-xs text-ink-500">Ficha #{op.ficha_id}</div>}
                </td>
                <td><span className="badge-neutral">{op.variante}</span></td>
                <td className="text-right">{Number(op.quantidade).toLocaleString('pt-BR')}</td>
                <td className="text-right">{Number(op.quantidade_produzida).toLocaleString('pt-BR')}</td>
                <td className="text-right text-rose-600">{Number(op.quantidade_refugo).toLocaleString('pt-BR')}</td>
                <td>{op.pedido_numero ? <span className="text-xs">#{op.pedido_numero}</span> : <span className="text-ink-400">-</span>}</td>
                <td className="text-right">{formatMoney(op.custo_previsto)}</td>
                <td className="text-right">{formatMoney(op.custo_real)}</td>
                <td>
                  <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COR[op.status] || 'bg-ink-100'}`}>
                    {op.status.replace('_', ' ')}
                  </span>
                </td>
                <td className="text-ink-600 text-xs">{formatDateTime(op.iniciada_em)}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <Link to={`/industrial/ordens/${op.id}`} className="btn-secondary !py-1 !px-2 text-xs">
                      <Eye size={12} />
                    </Link>
                    {op.status === 'proposta' && (
                      <>
                        <button onClick={() => aprovar.mutate(op.id)} disabled={aprovar.isPending}
                          className="btn-secondary !py-1 !px-2 text-xs text-emerald-700">
                          <ThumbsUp size={12} /> Aprovar
                        </button>
                        <button onClick={() => {
                          const m = prompt('Motivo da rejeição:')
                          if (m !== null) rejeitar.mutate({ id: op.id, motivo: m })
                        }} disabled={rejeitar.isPending}
                          className="btn-secondary !py-1 !px-2 text-xs text-rose-700">
                          <ThumbsDown size={12} />
                        </button>
                      </>
                    )}
                    {op.status === 'planejada' && (
                      <button onClick={() => setIniciarAlvo(op)} className="btn-secondary !py-1 !px-2 text-xs text-emerald-700">
                        <Play size={12} /> Iniciar
                      </button>
                    )}
                    {op.status === 'em_producao' && (
                      <button onClick={() => setConcluirAlvo(op)} className="btn-secondary !py-1 !px-2 text-xs text-brand-700">
                        <Check size={12} /> Concluir
                      </button>
                    )}
                    {(op.status === 'planejada' || op.status === 'em_producao') && (
                      <button onClick={() => setCancelarAlvo(op)} className="btn-secondary !py-1 !px-2 text-xs text-rose-700">
                        <Ban size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNova && <NovaOPModal onClose={() => setShowNova(false)} onCreated={() => {
        setShowNova(false)
        qc.invalidateQueries({ queryKey: ['ops'] })
      }} />}
      {iniciarAlvo && <IniciarModal op={iniciarAlvo} onClose={() => setIniciarAlvo(null)} onDone={() => {
        setIniciarAlvo(null)
        qc.invalidateQueries({ queryKey: ['ops'] })
      }} />}
      {concluirAlvo && <ConcluirModal op={concluirAlvo} onClose={() => setConcluirAlvo(null)} onDone={() => {
        setConcluirAlvo(null)
        qc.invalidateQueries({ queryKey: ['ops'] })
      }} />}
      {cancelarAlvo && <CancelarModal op={cancelarAlvo} onClose={() => setCancelarAlvo(null)} onDone={() => {
        setCancelarAlvo(null)
        qc.invalidateQueries({ queryKey: ['ops'] })
      }} />}
    </div>
  )
}

// ============================================================
// NOVA OP
// ============================================================
function NovaOPModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [produtoId, setProdutoId] = useState<number | ''>('')
  const [quantidade, setQuantidade] = useState('1')
  const [variante, setVariante] = useState('PADRAO')
  const [maquinaId, setMaquinaId] = useState<number | ''>('')
  const [moldeId, setMoldeId] = useState<number | ''>('')
  const [depositoId, setDepositoId] = useState<number | ''>('')
  const [pedidoId, setPedidoId] = useState<number | ''>('')
  const [observacao, setObservacao] = useState('')

  const { data: produtos = [] } = useQuery<Produto[]>({
    queryKey: ['produtos-select'],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { limit: 500 } })).data,
  })
  const { data: maquinas = [] } = useQuery<Maquina[]>({
    queryKey: ['maquinas-select'],
    queryFn: async () => (await api.get('/industrial/maquinas')).data,
  })
  const { data: moldes = [] } = useQuery<Molde[]>({
    queryKey: ['moldes-select'],
    queryFn: async () => (await api.get('/industrial/moldes')).data,
  })
  const { data: depositos = [] } = useQuery<Deposito[]>({
    queryKey: ['depositos-select'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })

  const produtoSel = useMemo(() => produtos.find(p => p.id === produtoId), [produtos, produtoId])

  // Variantes disponíveis da ficha do produto selecionado
  const { data: fichas = [] } = useQuery<any[]>({
    queryKey: ['fichas-select'],
    queryFn: async () => (await api.get('/industrial/fichas')).data,
  })
  const fichaDoProduto = useMemo(
    () => fichas.find(f => f.produto_id === produtoId),
    [fichas, produtoId],
  )

  const criar = useMutation({
    mutationFn: async () => (await api.post('/industrial/ordens', {
      produto_id: produtoId,
      quantidade: parseFloat(quantidade),
      variante,
      maquina_id: maquinaId || null,
      molde_id: moldeId || null,
      deposito_id: depositoId || null,
      pedido_id: pedidoId || null,
      observacao: observacao || null,
    })).data,
    onSuccess: (op: OP) => {
      toast.success(`OP #${op.numero} criada`)
      onCreated()
    },
  })

  const pronto = !!produtoId && parseFloat(quantidade) > 0

  return (
    <Modal onClose={onClose} title="Nova Ordem de Produção">
      <div className="space-y-3">
        <Field label="Produto *">
          <select className="input w-full" value={produtoId} onChange={e => setProdutoId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">— selecione —</option>
            {produtos.map(p => <option key={p.id} value={p.id}>{p.codigo} — {p.descricao}</option>)}
          </select>
        </Field>

        {produtoSel && (
          <div className="text-xs text-ink-600 bg-ink-50 p-2 rounded flex items-center gap-2">
            <Info size={14} />
            Estoque atual: <strong>{Number(produtoSel.estoque_atual).toLocaleString('pt-BR')}</strong>
            {!fichaDoProduto && <span className="text-amber-700 ml-2">⚠ produto sem ficha técnica ativa</span>}
            {fichaDoProduto && <span className="text-emerald-700 ml-2">✓ ficha #{fichaDoProduto.id}</span>}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Quantidade *">
            <input type="number" step="0.001" className="input w-full" value={quantidade} onChange={e => setQuantidade(e.target.value)} />
          </Field>
          <Field label="Variante">
            <input className="input w-full" value={variante} onChange={e => setVariante(e.target.value.toUpperCase())} placeholder="PADRAO" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Máquina">
            <select className="input w-full" value={maquinaId} onChange={e => setMaquinaId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— nenhuma —</option>
              {maquinas.filter(m => m.status === 'ATIVA').map(m => (
                <option key={m.id} value={m.id}>{m.codigo} — {m.nome} ({m.tipo})</option>
              ))}
            </select>
          </Field>
          <Field label="Molde">
            <select className="input w-full" value={moldeId} onChange={e => setMoldeId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— nenhum —</option>
              {moldes.map(m => <option key={m.id} value={m.id}>{m.codigo} — {m.descricao}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Depósito (destino do produto acabado)">
            <select className="input w-full" value={depositoId} onChange={e => setDepositoId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— default da empresa —</option>
              {depositos.filter(d => d.ativo).map(d => (
                <option key={d.id} value={d.id}>{d.nome}{d.is_default ? ' (default)' : ''}</option>
              ))}
            </select>
          </Field>
          <Field label="Pedido vinculado (opcional)">
            <input type="number" className="input w-full" value={pedidoId} onChange={e => setPedidoId(e.target.value ? Number(e.target.value) : '')} placeholder="ID do pedido" />
          </Field>
        </div>

        <Field label="Observação">
          <textarea className="input w-full" rows={2} value={observacao} onChange={e => setObservacao(e.target.value)} />
        </Field>
      </div>
      <ModalFooter>
        <button onClick={onClose} className="btn-secondary">Cancelar</button>
        <button disabled={!pronto || criar.isPending} onClick={() => criar.mutate()} className="btn-primary">
          {criar.isPending && <Loader2 size={14} className="animate-spin" />}
          Criar OP
        </button>
      </ModalFooter>
    </Modal>
  )
}

// ============================================================
// INICIAR OP — com preview
// ============================================================
function IniciarModal({ op, onClose, onDone }: { op: OP; onClose: () => void; onDone: () => void }) {
  const { data: preview, isLoading } = useQuery<Preview>({
    queryKey: ['op-preview', op.id],
    queryFn: async () => (await api.get(`/industrial/ordens/${op.id}/preview-consumo`)).data,
  })

  const iniciar = useMutation({
    mutationFn: async (forcar: boolean) =>
      (await api.post(`/industrial/ordens/${op.id}/iniciar`, null, { params: { forcar } })).data,
    onSuccess: () => {
      toast.success('OP iniciada — MP debitada do estoque')
      onDone()
    },
  })

  return (
    <Modal onClose={onClose} title={`Iniciar OP #${op.numero}`} wide>
      {isLoading && <div className="py-8 text-center text-ink-500">Calculando consumo…</div>}
      {preview && (
        <div className="space-y-3">
          {!preview.ficha_encontrada && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded p-3 flex gap-2">
              <AlertTriangle size={18} /> Produto sem ficha técnica — impossível iniciar.
            </div>
          )}
          {preview.alertas.length > 0 && preview.alertas.map((a, i) => (
            <div key={i} className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 text-sm flex gap-2">
              <AlertTriangle size={16} /> {a}
            </div>
          ))}

          <div className="text-sm text-ink-700">
            Variante <strong>{preview.variante}</strong> · Quantidade <strong>{preview.quantidade}</strong>
          </div>

          {preview.linhas.length > 0 && (
            <div className="table-wrap max-h-80 overflow-auto">
              <table className="table text-sm">
                <thead><tr>
                  <th>MP</th>
                  <th className="text-right">Necessário</th>
                  <th className="text-right">Saldo</th>
                  <th className="text-right">Custo</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {preview.linhas.map(l => (
                    <tr key={l.materia_prima_id} className={l.suficiente ? '' : 'bg-rose-50'}>
                      <td>
                        <div className="font-mono text-xs">{l.mp_codigo}</div>
                        <div>{l.mp_nome}</div>
                      </td>
                      <td className="text-right font-mono">{Number(l.quantidade_necessaria).toLocaleString('pt-BR')} {l.unidade}</td>
                      <td className="text-right font-mono">{Number(l.saldo_atual).toLocaleString('pt-BR')}</td>
                      <td className="text-right">{formatMoney(l.custo_total)}</td>
                      <td>{l.suficiente
                        ? <span className="text-emerald-600 text-xs">✓</span>
                        : <span className="text-rose-600 text-xs font-bold">FALTA</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold">
                    <td colSpan={3} className="text-right">Custo total previsto:</td>
                    <td className="text-right">{formatMoney(preview.custo_total)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {!preview.tudo_suficiente && preview.ficha_encontrada && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded p-3 text-sm">
              Saldo insuficiente em uma ou mais matérias-primas.
              Você pode <strong>forçar o início</strong> (estoque fica negativo) ou cancelar.
            </div>
          )}
        </div>
      )}
      <ModalFooter>
        <button onClick={onClose} className="btn-secondary">Cancelar</button>
        {preview?.tudo_suficiente && (
          <button disabled={iniciar.isPending} onClick={() => iniciar.mutate(false)} className="btn-primary">
            {iniciar.isPending && <Loader2 size={14} className="animate-spin" />}
            Confirmar início
          </button>
        )}
        {preview && !preview.tudo_suficiente && preview.ficha_encontrada && (
          <button disabled={iniciar.isPending} onClick={() => iniciar.mutate(true)} className="btn-danger">
            {iniciar.isPending && <Loader2 size={14} className="animate-spin" />}
            Forçar início (estoque negativo)
          </button>
        )}
      </ModalFooter>
    </Modal>
  )
}

// ============================================================
// CONCLUIR OP — múltiplas saídas
// ============================================================
interface ProdutoSug { id: number; codigo: string; descricao: string; unidade: string; is_principal: boolean; is_industrial: boolean }
interface LinhaSaida { produto_id: number | ''; quantidade: string; deposito_id: number | '' }

function ConcluirModal({ op, onClose, onDone }: { op: OP; onClose: () => void; onDone: () => void }) {
  const [saidas, setSaidas] = useState<LinhaSaida[]>([
    { produto_id: op.produto_id, quantidade: op.quantidade, deposito_id: op.deposito_id },
  ])
  const [qtdRef, setQtdRef] = useState('0')
  const [obs, setObs] = useState('')
  const [qualidade, setQualidade] = useState<'PRIMEIRA' | 'SEGUNDA' | 'TERCEIRA'>('PRIMEIRA')
  const [qualidadeMotivo, setQualidadeMotivo] = useState('')

  // Reseta state quando troca de OP (defesa contra remount otimizado do React)
  useEffect(() => {
    setSaidas([{ produto_id: op.produto_id, quantidade: op.quantidade, deposito_id: op.deposito_id }])
    setQtdRef('0')
    setObs('')
    setQualidade('PRIMEIRA')
    setQualidadeMotivo('')
  }, [op.id])

  // Sugestões de produto pra autocompletar (mesma categoria do produto principal)
  const { data: sug } = useQuery<{ produto_principal_id: number; sugeridos: ProdutoSug[] }>({
    queryKey: ['op-sug', op.id],
    queryFn: async () => (await api.get(`/industrial/produtos-sugeridos-saida/${op.id}`)).data,
  })

  // Depósitos ativos pra select
  const { data: depositos = [] } = useQuery<Deposito[]>({
    queryKey: ['depositos-select'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })

  const totalOk = saidas.reduce((s, l) => s + (parseFloat(l.quantidade) || 0), 0)
  const algumInvalido = saidas.some(l => !l.produto_id || parseFloat(l.quantidade) <= 0)

  function adicionarLinha() {
    setSaidas([...saidas, { produto_id: '', quantidade: '0', deposito_id: op.deposito_id }])
  }
  function removerLinha(idx: number) {
    setSaidas(saidas.filter((_, i) => i !== idx))
  }
  function alterar(idx: number, patch: Partial<LinhaSaida>) {
    setSaidas(saidas.map((l, i) => i === idx ? { ...l, ...patch } : l))
  }

  const concluir = useMutation({
    mutationFn: async () => {
      const obsFinal = qualidade !== 'PRIMEIRA' && qualidadeMotivo.trim()
        ? `${obs || ''}\n[QUALIDADE ${qualidade}] ${qualidadeMotivo.trim()}`.trim()
        : (obs || null)
      return (await api.post(`/industrial/ordens/${op.id}/concluir`, {
        saidas: saidas.map(l => ({
          produto_id: l.produto_id,
          quantidade: parseFloat(l.quantidade),
          deposito_id: l.deposito_id || null,
        })),
        quantidade_refugo: parseFloat(qtdRef || '0'),
        observacao: obsFinal,
        qualidade,
      })).data
    },
    onSuccess: () => {
      const n = saidas.length
      toast.success(`OP concluída — ${n} ${n === 1 ? 'produto entrou' : 'produtos entraram'} no estoque`)
      onDone()
    },
  })

  return (
    <Modal onClose={onClose} title={`Concluir OP #${op.numero}`} wide>
      <div className="space-y-3">
        <div className="text-sm text-ink-600">
          Quantidade planejada: <strong>{op.quantidade}</strong> ·
          Você pode dividir em múltiplos produtos acabados (ex: uma mistura vira vários solados).
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-semibold uppercase text-ink-700">Produtos acabados (saídas)</div>
            <button onClick={adicionarLinha} className="btn-secondary !py-1 !px-2 text-xs">
              <Plus size={12} /> Outra saída
            </button>
          </div>

          <div className="space-y-2">
            {saidas.map((l, idx) => (
              <div key={idx} className="border rounded p-2 bg-ink-50">
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6">
                    <div className="text-[10px] text-ink-500 uppercase mb-0.5">Produto</div>
                    <select className="input w-full text-sm" value={l.produto_id}
                      onChange={e => alterar(idx, { produto_id: e.target.value ? Number(e.target.value) : '' })}>
                      <option value="">— selecione —</option>
                      {sug?.sugeridos.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.is_principal ? '★ ' : ''}{p.codigo} — {p.descricao}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <div className="text-[10px] text-ink-500 uppercase mb-0.5">Quantidade</div>
                    <input type="number" step="0.001" className="input w-full text-sm"
                      value={l.quantidade} onChange={e => alterar(idx, { quantidade: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] text-ink-500 uppercase mb-0.5">Depósito</div>
                    <select className="input w-full text-sm" value={l.deposito_id}
                      onChange={e => alterar(idx, { deposito_id: e.target.value ? Number(e.target.value) : '' })}>
                      {depositos.filter(d => d.ativo).map(d => (
                        <option key={d.id} value={d.id}>{d.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-1 text-right">
                    {saidas.length > 1 && (
                      <button onClick={() => removerLinha(idx)} className="text-rose-600 hover:bg-rose-100 rounded p-1">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center mt-2 text-sm text-ink-700">
            <span>Total entrando no estoque:</span>
            <strong className="text-base">{totalOk.toLocaleString('pt-BR')}</strong>
          </div>
        </div>

        <div className="pt-2 border-t">
          <div className="text-xs font-semibold uppercase text-ink-700 mb-1.5">Qualidade do lote <span className="text-red-600">*</span></div>
          <div className="flex gap-2 mb-2">
            {(() => {
              // Classes ESTÁTICAS por qualidade — Tailwind precisa ver o nome inteiro
              const STYLES = {
                PRIMEIRA: {
                  ativo: 'bg-emerald-600 text-white border-emerald-600 shadow-sm',
                  inativo: 'bg-white text-ink-700 border-ink-300 hover:border-emerald-400 hover:text-emerald-700',
                  emoji: '🟢',
                },
                SEGUNDA: {
                  ativo: 'bg-amber-500 text-white border-amber-500 shadow-sm',
                  inativo: 'bg-white text-ink-700 border-ink-300 hover:border-amber-400 hover:text-amber-700',
                  emoji: '🟡',
                },
                TERCEIRA: {
                  ativo: 'bg-red-600 text-white border-red-600 shadow-sm',
                  inativo: 'bg-white text-ink-700 border-ink-300 hover:border-red-400 hover:text-red-700',
                  emoji: '🔴',
                },
              } as const
              return (['PRIMEIRA', 'SEGUNDA', 'TERCEIRA'] as const).map(q => {
                const ativo = qualidade === q
                const s = STYLES[q]
                return (
                  <button key={q} type="button" onClick={() => setQualidade(q)}
                    className={
                      'flex-1 py-2 rounded-lg text-sm font-semibold border transition ' +
                      (ativo ? s.ativo : s.inativo)
                    }>
                    {s.emoji} {q}
                  </button>
                )
              })
            })()}
          </div>
          {qualidade !== 'PRIMEIRA' && (
            <>
              <input
                type="text"
                value={qualidadeMotivo}
                onChange={e => setQualidadeMotivo(e.target.value)}
                placeholder={`Motivo de ${qualidade.toLowerCase()} (ex: bolha lateral 5mm, dureza Shore 65)`}
                className="input w-full text-sm"
                required
                aria-required="true"
              />
              {!qualidadeMotivo.trim() && (
                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                  ⚠ Motivo é obrigatório quando qualidade ≠ PRIMEIRA
                </p>
              )}
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 pt-2 border-t">
          <Field label="Refugo (descartado)">
            <input type="number" step="0.001" className="input w-full"
              value={qtdRef} onChange={e => setQtdRef(e.target.value)} />
          </Field>
          <Field label="Observação">
            <input className="input w-full" value={obs} onChange={e => setObs(e.target.value)} placeholder="opcional" />
          </Field>
        </div>

        {totalOk > 0 && Number(op.custo_real) > 0 && (
          <div className="text-xs text-ink-600 bg-brand-50 border border-brand-200 p-2 rounded">
            <strong>Custo real (R$ {Number(op.custo_real).toFixed(2)})</strong> será rateado proporcionalmente entre as saídas — cada produto fica com custo unit. ≈ R$ {(Number(op.custo_real) / totalOk).toFixed(4)} por unidade nesta OP.
          </div>
        )}
      </div>
      <ModalFooter>
        <button onClick={onClose} className="btn-secondary">Cancelar</button>
        <button disabled={
            concluir.isPending || algumInvalido || totalOk <= 0 ||
            (qualidade !== 'PRIMEIRA' && !qualidadeMotivo.trim())
          }
          onClick={() => concluir.mutate()} className="btn-primary">
          {concluir.isPending && <Loader2 size={14} className="animate-spin" />}
          Concluir
        </button>
      </ModalFooter>
    </Modal>
  )
}

// ============================================================
// CANCELAR OP
// ============================================================
function CancelarModal({ op, onClose, onDone }: { op: OP; onClose: () => void; onDone: () => void }) {
  const [motivo, setMotivo] = useState('')
  const cancelar = useMutation({
    mutationFn: async () => (await api.post(`/industrial/ordens/${op.id}/cancelar`, { motivo: motivo || null })).data,
    onSuccess: () => {
      toast.success(op.status === 'em_producao' ? 'OP cancelada — MP estornada' : 'OP cancelada')
      onDone()
    },
  })
  return (
    <Modal onClose={onClose} title={`Cancelar OP #${op.numero}`}>
      <div className="space-y-3">
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded text-sm">
          {op.status === 'em_producao'
            ? 'Esta OP está em produção. Cancelar vai ESTORNAR o consumo de matéria-prima.'
            : 'Cancelar esta OP não muda estoque (ainda não havia sido iniciada).'}
        </div>
        <Field label="Motivo (opcional)">
          <textarea className="input w-full" rows={3} value={motivo} onChange={e => setMotivo(e.target.value)} />
        </Field>
      </div>
      <ModalFooter>
        <button onClick={onClose} className="btn-secondary">Voltar</button>
        <button disabled={cancelar.isPending} onClick={() => cancelar.mutate()} className="btn-danger">
          {cancelar.isPending && <Loader2 size={14} className="animate-spin" />}
          Confirmar cancelamento
        </button>
      </ModalFooter>
    </Modal>
  )
}

// ============================================================
// Componentes de layout de modal
// ============================================================
function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl ${wide ? 'max-w-3xl' : 'max-w-xl'} w-full max-h-[90vh] overflow-auto`}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose} className="text-ink-500 hover:text-ink-900"><X size={18} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  )
}

function ModalFooter({ children }: { children: React.ReactNode }) {
  return <div className="border-t px-5 py-3 flex justify-end gap-2 mt-2 -mx-5 -mb-4 bg-ink-50">{children}</div>
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-ink-700 mb-1">{label}</div>
      {children}
    </label>
  )
}
